import { type RefObject, useEffect, useRef, useState } from 'react';
import { isPhone } from './usePhone';

/** How far the finger moves before letting go refreshes, and how far the indicator follows. */
export const PULL_TRIGGER_PX = 64;
const PULL_MAX_PX = 96;

/**
 * Pull down at the top of a list on a phone to refresh it. Only touch, only in the phone layout,
 * and only when the list is scrolled to the top; everything else scrolls as before.
 */
export function usePullToRefresh(
  ref: RefObject<HTMLElement | null>,
  onRefresh: () => Promise<unknown> | unknown,
  /** Changes when the list element appears (it may render after loading). */
  mounted: unknown = true,
): { pull: number; refreshing: boolean } {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const latest = useRef(onRefresh);
  latest.current = onRefresh;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let startY: number | null = null;
    let distance = 0;
    let busy = false;
    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      startY = !busy && isPhone() && el.scrollTop <= 0 && touch ? touch.clientY : null;
      distance = 0;
    };
    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (startY === null || !touch) return;
      const dy = touch.clientY - startY;
      // Scrolling up, or the list moved away from the top: this is no pull.
      if (dy <= 0 || el.scrollTop > 0) {
        startY = null;
        distance = 0;
        setPull(0);
        return;
      }
      distance = Math.min(dy * 0.5, PULL_MAX_PX);
      setPull(distance);
    };
    const onEnd = () => {
      if (startY === null) return;
      startY = null;
      if (distance < PULL_TRIGGER_PX) {
        setPull(0);
        return;
      }
      busy = true;
      setRefreshing(true);
      void Promise.resolve()
        .then(() => latest.current())
        .catch(() => undefined)
        .finally(() => {
          busy = false;
          setRefreshing(false);
          setPull(0);
        });
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [ref, mounted]);

  return { pull, refreshing };
}
